/**
 * Shared "Type" step for the typed encounter forms. Wraps
 * `EncounterTypeSection` with the standard option lists and the
 * recent-intake duplicate guardrail (clinical intake only).
 *
 * Each typed form's Type step content collapses from ~16 LOC of inline
 * JSX to a single-line `<EncounterTypeStep ... />` invocation.
 *
 * @agentic connections
 */

import { useMemo } from "react";

import type { TypedEncounterCode } from "@benchmark/core/types";

import { EncounterTypeSection } from "../sections/EncounterTypeSection";
import { buildAllTypeOptions, buildCurrentTypeOptions } from "./encounterIcons";

/**
 * Props for {@link EncounterTypeStep}.
 *
 * @agentic connections
 */
export type EncounterTypeStepProps = {
  /** Current discriminator value (e.g. `"general-interaction"`). */
  value: TypedEncounterCode | "";
  /** Whether the field is read-only (edit / activity-fixed type). */
  readOnly?: boolean;
  /**
   * Optional explicit allow-list. Defaults to `[value]` so the typed form's
   * Type step shows only its own type when the step is visible (free
   * creation handed by the unified picker form).
   */
  allowedTypes?: string[];
  /** Recent-intake duplicate guardrail (clinical intake only). */
  duplicateIntakeEncounterId?: string;
  /** Form-level error string for the field. */
  error?: string;
  /** Called when the user picks a different type. */
  onChange: (value: string) => void;
};

/**
 * Renders the Type step of a typed encounter form. The card grid uses the
 * v2 type options; the all-types option list is provided to the section
 * so the legacy "(Legacy)" labels can render in edge cases (kept for
 * parity with the legacy form which mounts the section directly).
 *
 * @agentic connections
 */
export const EncounterTypeStep = ({
  value,
  readOnly = false,
  allowedTypes,
  duplicateIntakeEncounterId,
  error,
  onChange,
}: EncounterTypeStepProps) => {
  const allOptions = useMemo(() => buildAllTypeOptions(), []);
  const currentOptions = useMemo(() => buildCurrentTypeOptions(), []);
  const effectiveAllowed = allowedTypes ?? (value ? [value] : []);

  return (
    <EncounterTypeSection
      value={value}
      hasLegacyEncounterType={false}
      allEncounterTypeOptions={allOptions}
      currentEncounterTypeOptions={currentOptions}
      allowedTypes={effectiveAllowed}
      error={error}
      readOnly={readOnly}
      duplicateIntakeEncounterId={duplicateIntakeEncounterId}
      onChange={onChange}
    />
  );
};
